// Relics (docs/game-design.md, "Реликвии"): four unique team prizes, an all-in gamble. Each exists once per
// match across both teams; a team holds at most one; buying one (RELIC_SHOP.price ink from the team pool, base
// only, never sold) gives it to BOTH teammates at once, and the first holder's death knocks it out of the team
// back into the shop, where anyone — the losers included — may buy it again. So the state is per TEAM, not per hero:
// `Game.relics[team]` is the held line plus its one shared cooldown (either teammate presses T), and "both
// hold it" needs nothing to keep in sync. Same circular-import rule as artifacts.ts: game.js's values are only
// ever read inside function bodies here.
import type { Team } from './layout.js';
import type { Game, Hero } from './game.js';
import type { RelicLine } from './commands.js';
import type { GameEvent } from './events.js';
import { inOwnBase, maxHpOf } from './artifacts.js';
import { isHero, type Target } from './monsters.js';

const flatDist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.hypot(a.x - b.x, a.z - b.z);
const other = (t: Team): Team => (t === 'a' ? 'b' : 'a');

/* ---------------- data table ---------------- */
// `ai`: bot triggers, same convention as ARTIFACTS — see botRelic() at the bottom.
export const RELICS = {
  // 5 s of invulnerability for the caster, and for a living teammate within `r` of him; both keep attacking
  // and casting (unlike Пергаментный плащ, it is not a dodge — only damage and CC stop landing).
  martyrShield: { kind: 'active', cd: 60, dur: 5, r: 20, ai: { hurt: 0.4 } },
  // Always on for both: move speed and attack speed, summed in with the other additive bonuses.
  wildHorn: { kind: 'passive', moveSpeed: 0.35, atkSpeed: 0.35 },
  // For `dur` seconds the team sees the whole map: no fog, no grass, Слиться and enemy totems included (armed
  // traps stay hidden — they are never sent to the enemy at all). While open, the team's heroes deal `dmgBonus`
  // more to every enemy hero (see eyeDmgMultOf). ai: a contested point within `near`.
  bestiaryEye: { kind: 'active', cd: 30, dur: 6, dmgBonus: 0.1, cdRate: 0.15, ai: { near: 14 } },
  // Always on for both, in combat and out: this share of max hp per second on top of base regen, through
  // heal() — so Бессонница denies it and Смоляная бутыль cuts it like any other heal.
  hydraHeart: { kind: 'passive', regenPct: 0.05 },
} as const satisfies Record<RelicLine, any>;

export const RELIC_LINES = Object.keys(RELICS) as RelicLine[];
// `botReserve`: the pool a bot wants before buying one while its own artefact slots are not both filled yet.
export const RELIC_SHOP = { price: 500, botReserve: 650 } as const;

/** A team's relic: the line and its one cooldown, shared by both teammates. Runs at plain game speed —
    Песочные часы is one hero's, the relic the team's. */
export type RelicHold = { line: RelicLine; cd: number };

/* ---------------- reads ---------------- */

export function relicOf(g: Game, team: Team): RelicLine | null { return g.relics[team]?.line ?? null; }
/** Which team holds `line` right now, or null while it sits in the shop. */
export function relicHolder(g: Game, line: RelicLine): Team | null {
  return g.relics.a?.line === line ? 'a' : g.relics.b?.line === line ? 'b' : null;
}
/** Рог Дикой Охоты: additive move-speed fraction for `h` (0 without it). */
export function relicMoveSpeedBonusOf(g: Game, h: Hero): number {
  return relicOf(g, h.team) === 'wildHorn' ? RELICS.wildHorn.moveSpeed : 0;
}
/** Рог Дикой Охоты: additive attack-speed fraction for `h`, summed with attackSpeedMultOf(). */
export function relicAtkSpeedBonusOf(g: Game, h: Hero): number {
  return relicOf(g, h.team) === 'wildHorn' ? RELICS.wildHorn.atkSpeed : 0;
}
/** Сердце Гидры: hp per second for `h` (0 without it). */
export function relicRegenOf(g: Game, h: Hero): number {
  return relicOf(g, h.team) === 'hydraHeart' ? RELICS.hydraHeart.regenPct * maxHpOf(h) : 0;
}
/** The one team allowed to see this event, or null when the ordinary fog rule applies: an Око cast is its own
    team's secret — the enemy watching the caster must not learn when it was opened (and so its cooldown). */
export function eventOnlyFor(g: Game, e: GameEvent): Team | null {
  return e.type === 'fx' && e.name === 'bestiaryEye' ? g.heroes[e.hero]?.team ?? null : null;
}
/** Око Бестиария is open for `team` right now. */
export function eyeOpen(g: Game, team: Team): boolean { return g.t < g.eyeUntil[team]; }
/** Око Бестиария: damage multiplier for `src` hitting `target` — the bonus while his team's Eye is open, on
    enemy heroes only. */
export function eyeDmgMultOf(g: Game, src: Hero | null, target: Target): number {
  return src && isHero(target) && target.team !== src.team && eyeOpen(g, src.team) ? 1 + RELICS.bestiaryEye.dmgBonus : 1;
}

/** Око Бестиария: extra cooldown speed (added to Песочные часы' rate) for the team's heroes while it is open. */
export function eyeCdRateOf(g: Game, h: Hero): number { return eyeOpen(g, h.team) ? RELICS.bestiaryEye.cdRate : 0; }

/* ---------------- shop ---------------- */

export function buyRelic(g: Game, h: Hero, line: RelicLine): boolean {
  if (!h.alive || !inOwnBase(h) || g.relics[h.team] || relicHolder(g, line)) return false;
  if (g.ink[h.team] < RELIC_SHOP.price) return false;
  g.ink[h.team] -= RELIC_SHOP.price;
  g.relics[h.team] = { line, cd: 0 };
  g.stats.relicBought(h.id, h.team, line, RELIC_SHOP.price);
  g.emit({ type: 'relic', team: h.team, line, action: 'bought' });
  return true;
}

/** Game.kill() calls this for every death: a holder dying knocks the relic out of his team. What it already
    did stays done — a Щит already granted and an Око already open run their course. */
export function loseRelicOnDeath(g: Game, h: Hero) {
  const held = g.relics[h.team];
  if (!held) return;
  g.relics[h.team] = null;
  g.stats.relicLost(h.team);
  g.emit({ type: 'relic', team: h.team, line: held.line, action: 'lost' });
}

/* ---------------- casting (T) ---------------- */

export function canCastRelic(g: Game, h: Hero): boolean {
  const held = g.relics[h.team];
  if (!held || RELICS[held.line].kind !== 'active' || held.cd > 0) return false;
  // The same hero-state gates as an active artefact (canCastArtifact).
  if (!h.alive || g.t < h.stunUntil || h.state !== 'normal' || g.taunted(h) || g.forcedKind(h) || h.pull) return false;
  return g.t >= h.cloakUntil;
}

export function castRelic(g: Game, h: Hero): boolean {
  if (!canCastRelic(g, h)) return false;
  g.stir(h);
  const held = g.relics[h.team]!;
  if (held.line === 'martyrShield') {
    const cfg = RELICS.martyrShield;
    for (const o of g.heroes) {
      if (o.team !== h.team || !o.alive || (o !== h && flatDist(o.pos, h.pos) > cfg.r)) continue;
      o.invulnUntil = Math.max(o.invulnUntil, g.t + cfg.dur);
      g.emit({ type: 'fx', name: 'martyrShield', hero: o.id });
    }
    held.cd = cfg.cd;
  } else if (held.line === 'bestiaryEye') {
    const cfg = RELICS.bestiaryEye;
    g.eyeUntil[h.team] = g.t + cfg.dur;
    g.emit({ type: 'fx', name: 'bestiaryEye', hero: h.id });
    held.cd = cfg.cd;
  }
  return true;
}

export function updateRelics(g: Game, dt: number) {
  for (const team of ['a', 'b'] as const) {
    const held = g.relics[team];
    if (held && held.cd > 0) held.cd = Math.max(0, held.cd - dt);
  }
}

/* ---------------- bots ---------------- */

/** What a bot would buy: nothing while its team holds one or the pool is short of the price plus a margin —
    RELIC_SHOP.price once the bot's own two slots are full, RELIC_SHOP.botReserve before (the relic must not
    starve its artefacts). Behind on score → Щит; a duelist → Рог; a guard or support (the tanky and the
    frail, who live off sustain) → Гидра; otherwise the first free line of FALLBACK. */
export function botRelicChoice(g: Game, h: Hero): RelicLine | null {
  if (g.relics[h.team]) return null;
  const pool = g.ink[h.team], full = !!h.artifacts.E && !!h.artifacts.R;
  if (pool < RELIC_SHOP.price || (!full && pool < RELIC_SHOP.botReserve)) return null;
  const free = (line: RelicLine) => !relicHolder(g, line);
  const behind = g.score[h.team] < g.score[other(h.team)];
  if (behind && free('martyrShield')) return 'martyrShield';
  // Око sees through Слиться: against a Тень it outranks the role picks.
  if (free('bestiaryEye') && g.heroes.some(e => e.team !== h.team && e.kind === 'shadow')) return 'bestiaryEye';
  if (h.def.role === 'duelist' && free('wildHorn')) return 'wildHorn';
  if ((h.def.role === 'guard' || h.def.role === 'support') && free('hydraHeart')) return 'hydraHeart';
  return FALLBACK.find(free) ?? null;
}
const FALLBACK: readonly RelicLine[] = ['hydraHeart', 'wildHorn', 'martyrShield', 'bestiaryEye'];

/** Щит: in a fight, with the bot or a teammate within reach under `hurt` of max hp. Око: a contested point
    within `near` while some enemy is out of sight, or a fight starting against a team with a Тень. */
export function botRelic(g: Game, h: Hero, foe?: Hero) {
  const held = g.relics[h.team];
  if (!held || !canCastRelic(g, h)) return;
  if (held.line === 'martyrShield') {
    const cfg = RELICS.martyrShield;
    const hurt = g.heroes.some(a => a.team === h.team && a.alive && (a === h || flatDist(a.pos, h.pos) <= cfg.r) && a.hp < maxHpOf(a) * cfg.ai.hurt);
    if (foe && hurt) castRelic(g, h);
  } else if (held.line === 'bestiaryEye') {
    const enemies = g.heroes.filter(e => e.team !== h.team && e.alive);
    const unseen = enemies.some(e => !g.visibleTo(h.team, e));
    const contested = g.points.some(p => p.contested && flatDist(p.pos, h.pos) <= RELICS.bestiaryEye.ai.near);
    const shadow = !!foe && enemies.some(e => e.kind === 'shadow');
    if ((contested && unseen) || shadow) castRelic(g, h);
  }
}
